/**
 * Drawer section 3 (#1911) — GATE ONE: see the list before it is sent to.
 *
 * The largest risk in a hand-driven bulk send is finding out who it reached by
 * reading the replies. So this section does three things and none of them are
 * conveniences: it shows every address, it exports them, and it lets an
 * operator strike rows out one by one before anything is frozen.
 *
 * Preview and freeze are two different acts against two different instants.
 * Preview writes nothing, so recomputing is free and an operator can look as
 * often as they like. Freeze copies the kept rows into `email_notice_recipients`
 * and those rows — not a join re-run at send time — are what gets mailed. The
 * exclusion set is therefore local state right up until the freeze, and it is
 * dropped whenever the candidate list is recomputed: a strike-out is an
 * operator's judgement about a specific row in a specific list, and silently
 * carrying it onto a differently-computed list would apply that judgement to
 * something they never saw.
 */
import { useMemo, useState } from 'react';
import type {
  EmailNotice,
  EmailNoticeRecipient,
  EmailNoticeRecipientCandidate,
} from '@nadoutong/contracts';
import { useT } from '../../i18n/index.ts';
import {
  Button,
  Chip,
  Table,
  TableEmpty,
  TBody,
  Td,
  Th,
  THead,
  Tr,
} from '../../ui/index.ts';
import { SectionCaption } from './SectionCaption.tsx';

interface Props {
  readonly notice: EmailNotice;
  readonly frozen: readonly EmailNoticeRecipient[];
  readonly candidates: readonly EmailNoticeRecipientCandidate[] | null;
  readonly windowFrom: string | null;
  readonly windowDays: number | null;
  readonly previewing: boolean;
  readonly finalizing: boolean;
  /**
   * The form's audience differs from the stored one (#1920).
   *
   * The freeze recomputes SERVER-SIDE from the stored audience, while the
   * preview above answers for what is on screen. With unsaved audience edits
   * the two resolve to different populations, so freezing here would commit a
   * list the operator never looked at while a different count is still in front
   * of them. Refused rather than warned: the number they are working from is
   * the number they decide to send on.
   */
  readonly audienceDirty: boolean;
  readonly onPreview: () => void;
  readonly onFinalize: (excludeAccountIds: readonly string[]) => void;
  readonly onExport: (rows: readonly { readonly account_id: string; readonly email: string }[]) => void;
}

export function NoticeRecipientsSection({
  notice,
  frozen,
  candidates,
  windowFrom,
  windowDays,
  previewing,
  finalizing,
  audienceDirty,
  onPreview,
  onFinalize,
  onExport,
}: Props): JSX.Element {
  const t = useT();
  const [excluded, setExcluded] = useState<readonly string[]>([]);
  const excludedSet = useMemo(() => new Set(excluded), [excluded]);
  const editable = notice.status === 'draft';

  const kept = (candidates ?? []).filter((row) => !excludedSet.has(row.account_id));

  const toggle = (accountId: string): void => {
    setExcluded((prev) =>
      prev.includes(accountId)
        ? prev.filter((entry) => entry !== accountId)
        : [...prev, accountId],
    );
  };

  const recompute = (): void => {
    // Exclusions are dropped BEFORE the new list arrives, not merged into it.
    setExcluded([]);
    onPreview();
  };

  return (
    <section className="flex flex-col gap-3" data-testid="email-notice-section-recipients">
      <SectionCaption
        index={3}
        title={t('email_notices.section.recipients')}
        hint={t('email_notices.section.recipients_hint')}
      />

      <div className="flex flex-wrap items-center gap-2">
        <Chip
          tone={notice.recipients_finalized_at === null ? 'neutral' : 'ok'}
          testId="email-notice-gate-recipients"
        >
          {notice.recipients_finalized_at === null
            ? t('email_notices.gate.recipients_open')
            : t('email_notices.gate.recipients_frozen', { count: String(frozen.length) })}
        </Chip>
        <Button
          size="sm"
          variant="secondary"
          loading={previewing}
          onClick={recompute}
          data-testid="email-notice-recompute"
        >
          {candidates === null
            ? t('email_notices.recipients.compute')
            : t('email_notices.recipients.recompute')}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={candidates === null && frozen.length === 0}
          onClick={() => onExport(candidates === null ? frozen : kept)}
          data-testid="email-notice-export"
        >
          {t('email_notices.recipients.export')}
        </Button>
      </div>

      <p className="m-0 text-micro text-paper-low" data-testid="email-notice-window-note">
        {t('email_notices.recipients.window', {
          days: windowDays === null ? '—' : String(windowDays),
          from: windowFrom ?? '—',
        })}
      </p>

      {candidates === null ? (
        <FrozenTable rows={frozen} />
      ) : (
        <>
          <p
            className="m-0 text-label text-paper-mid"
            data-testid="email-notice-candidate-summary"
          >
            {t('email_notices.recipients.summary', {
              total: String(candidates.length),
              excluded: String(excluded.length),
              kept: String(kept.length),
            })}
          </p>
          <div className="max-h-72 overflow-y-auto">
            <Table testId="email-notice-candidates">
              <THead>
                <Tr interactive={false}>
                  <Th>{t('email_notices.recipients.column.email')}</Th>
                  <Th>{t('email_notices.recipients.column.brand')}</Th>
                  <Th num>{t('email_notices.recipients.column.requests')}</Th>
                  <Th>{t('email_notices.recipients.column.last_used')}</Th>
                  <Th>{t('email_notices.recipients.column.action')}</Th>
                </Tr>
              </THead>
              <TBody>
                {candidates.length === 0 ? (
                  <TableEmpty colSpan={5}>{t('email_notices.recipients.none')}</TableEmpty>
                ) : (
                  candidates.map((row) => {
                    const struck = excludedSet.has(row.account_id);
                    return (
                      <Tr key={row.account_id} interactive={false}>
                        <Td mono className={struck ? 'line-through text-paper-faint' : undefined}>
                          {row.email}
                        </Td>
                        <Td>{row.brand}</Td>
                        <Td num mono>
                          {row.request_count}
                        </Td>
                        <Td mono>{row.last_used_at.slice(0, 10)}</Td>
                        <Td>
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={!editable}
                            onClick={() => toggle(row.account_id)}
                            data-testid={`email-notice-exclude-${row.account_id}`}
                          >
                            {struck
                              ? t('email_notices.recipients.restore')
                              : t('email_notices.recipients.exclude')}
                          </Button>
                        </Td>
                      </Tr>
                    );
                  })
                )}
              </TBody>
            </Table>
          </div>
          <div>
            <Button
              size="sm"
              loading={finalizing}
              disabled={!editable || audienceDirty || kept.length === 0}
              onClick={() => onFinalize(excluded)}
              data-testid="email-notice-finalize"
            >
              {t('email_notices.recipients.finalize', { count: String(kept.length) })}
            </Button>
            {editable && audienceDirty ? (
              <p
                className="m-0 pt-1 text-micro text-warn"
                data-testid="email-notice-finalize-blocked-dirty"
              >
                {t('email_notices.recipients.blocked_audience_dirty')}
              </p>
            ) : null}
            {editable ? null : (
              <p className="m-0 pt-1 text-micro text-paper-low">
                {t('email_notices.recipients.locked')}
              </p>
            )}
          </div>
        </>
      )}
    </section>
  );
}

/**
 * What is actually going to be mailed, once the list is frozen.
 *
 * ── THIS IS LOAD-BEARING, NOT A SECOND LOOK AT THE PREVIEW ──────────────────
 * It looks like a duplicate of the candidate table above it and it is not. The
 * preview and the freeze run against two different instants, and the freeze
 * recomputes rather than committing the previewed rows — so an account that
 * started calling the model in between appears HERE and in no earlier surface.
 * The operator has never seen it, never had the chance to strike it out, and
 * this is the last screen before an unrecallable send.
 *
 * Deleting this because "the list was already reviewed in the preview" would
 * quietly retire the "recipients are visible before sending" guarantee for
 * exactly the rows nobody reviewed. The guarantee is currently carried by this
 * table AND by the delivery table under section 4; at least one of them must
 * keep rendering the frozen rows. `email-notices-page.test.tsx` →
 * "an address that joined after the preview is still visible before any send"
 * asserts the property rather than either component, so it survives moving the
 * rendering and fails if both go.
 */
function FrozenTable({ rows }: { readonly rows: readonly EmailNoticeRecipient[] }): JSX.Element {
  const t = useT();
  return (
    <div className="max-h-72 overflow-y-auto">
      <Table testId="email-notice-frozen-recipients">
        <THead>
          <Tr interactive={false}>
            <Th>{t('email_notices.recipients.column.email')}</Th>
            <Th>{t('email_notices.recipients.column.brand')}</Th>
            <Th>{t('email_notices.recipients.column.status')}</Th>
          </Tr>
        </THead>
        <TBody>
          {rows.length === 0 ? (
            <TableEmpty colSpan={3}>{t('email_notices.recipients.not_computed')}</TableEmpty>
          ) : (
            rows.map((row) => (
              <Tr key={row.account_id} interactive={false}>
                <Td mono>{row.email}</Td>
                <Td>{row.brand}</Td>
                <Td>{row.status}</Td>
              </Tr>
            ))
          )}
        </TBody>
      </Table>
    </div>
  );
}
